// frontend/src/StoreLocator/LocationPage.jsx — /store-locator/locations/<id> and /store-locator/new: every field on one page.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Combobox, InlineStack, Listbox, Select, Tag } from '@shopify/polaris';
import { StatusPill } from '../ui';
import { Alert } from '../GiftCards/common';
import { slApi } from './apiClient';
import { DAYS, DAY_NAMES, clock, emptyHours, span } from './common';

const TEXT = ['name', 'handle', 'address_line_1', 'address_line_2', 'postal_code', 'city', 'region', 'country', 'phone', 'email', 'website', 'page_title',
  'page_description', 'meta_title', 'meta_description', 'external_id', 'designation', 'announcements', 'button_title_1', 'button_url_1', 'button_title_2', 'button_url_2', 'timezone', 'image_url'];
const blank = () => ({ ...Object.fromEntries(TEXT.map((k) => [k, ''])), status: 'published', priority: 0, latitude: '', longitude: '', tags: [], hours: emptyHours(), country: 'United States' });

function Field({ label, children, span2, hint }) {
  return <label className={`uh-ia-fld${span2 ? ' uh-span2' : ''}`}><span className="lab">{label}</span>{children}{hint && <span className="uh-ia-hint">{hint}</span>}</label>;
}

// Quarter-hour choices from 5:00 AM to 11:45 PM, stored as HH:MM.
const TIME_OPTIONS = [];
for (let m = 5 * 60; m < 24 * 60; m += 15) {
  const hh = String(Math.floor(m / 60)).padStart(2, '0'); const mm = String(m % 60).padStart(2, '0');
  TIME_OPTIONS.push({ value: `${hh}:${mm}`, label: clock(`${hh}:${mm}`) });
}
const timeOptions = (v) => (v && !TIME_OPTIONS.some((o) => o.value === v) ? [{ value: v, label: clock(v) }, ...TIME_OPTIONS] : TIME_OPTIONS);
const STATUS_OPTIONS = [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: '24h', label: '24 hours' }];

function HoursGrid({ hours, onChange, others, selfId }) {
  const setDay = (d, patch) => onChange({ ...hours, [d]: { ...hours[d], ...patch } });
  const copyMonday = () => onChange({ ...hours, tue: { ...hours.mon }, wed: { ...hours.mon }, thu: { ...hours.mon }, fri: { ...hours.mon } });
  const [from, setFrom] = useState('');
  const copyFrom = (id) => { setFrom(id); const src = (others || []).find((l) => String(l.id) === id); if (src && src.hours) { onChange({ ...emptyHours(), ...src.hours }); setFrom(''); } };
  const choices = [{ value: '', label: 'Copy hours from another store…' }, ...(others || []).filter((l) => l.id !== selfId).map((l) => ({ value: String(l.id), label: l.name }))];
  return (
    <div>
      <div className="uh-sl-hours">
        <span /><span className="uh-ia-hint">Status</span><span className="uh-ia-hint">Opens</span><span className="uh-ia-hint">Closes</span>
        {DAYS.map((d) => {
          const h = hours[d] || { status: 'closed' };
          return (
            <React.Fragment key={d}>
              <span className="uh-sl-day">{DAY_NAMES[d]}</span>
              <Select label={`${DAY_NAMES[d]} status`} labelHidden options={STATUS_OPTIONS} value={h.status} onChange={(v) => setDay(d, { status: v, open: h.open || '10:00', close: h.close || '18:00' })} />
              <Select label={`${DAY_NAMES[d]} opens`} labelHidden options={timeOptions(h.open)} value={h.status === 'open' ? h.open || '10:00' : ''} disabled={h.status !== 'open'} onChange={(v) => setDay(d, { open: v })} placeholder={h.status === 'open' ? undefined : '—'} />
              <Select label={`${DAY_NAMES[d]} closes`} labelHidden options={timeOptions(h.close)} value={h.status === 'open' ? h.close || '18:00' : ''} disabled={h.status !== 'open'} onChange={(v) => setDay(d, { close: v })} placeholder={h.status === 'open' ? undefined : '—'} />
            </React.Fragment>
          );
        })}
      </div>
      <div className="uh-ia-row" style={{ marginTop: 10, gap: 10 }}>
        <Button onClick={copyMonday}>Copy Monday to weekdays</Button>
        <div style={{ minWidth: 280 }}><Select label="Copy hours from another store" labelHidden options={choices} value={from} onChange={copyFrom} /></div>
      </div>
    </div>
  );
}

// Polaris tags: the ones on the store as removable tags, a combobox to add one from the list or type a new one.
function TagsField({ tags, all, onChange }) {
  const [text, setText] = useState('');
  const options = useMemo(() => all.filter((t) => !tags.includes(t) && t.toLowerCase().includes(text.trim().toLowerCase())), [all, tags, text]);
  const typed = text.trim();
  const add = (v) => { const t = (v || '').trim(); if (t && !tags.includes(t)) onChange([...tags, t]); setText(''); };
  const canAddTyped = typed && !tags.includes(typed) && !all.some((t) => t.toLowerCase() === typed.toLowerCase());
  return (
    <div className="uh-sl-tags">
      <InlineStack gap="200" blockAlign="center" wrap>
        {tags.map((t) => <Tag key={t} onRemove={() => onChange(tags.filter((x) => x !== t))}>{t}</Tag>)}
        <div style={{ minWidth: 220, flex: '1 1 220px' }}>
          <Combobox activator={<Combobox.TextField label="Add tag" labelHidden value={text} onChange={setText} placeholder="Add tag…" autoComplete="off" />}>
            {(options.length > 0 || canAddTyped) && (
              <Listbox onSelect={add}>
                {options.map((t) => <Listbox.Option key={t} value={t}>{t}</Listbox.Option>)}
                {canAddTyped && <Listbox.Action value={typed}>{`Add "${typed}"`}</Listbox.Action>}
              </Listbox>
            )}
          </Combobox>
        </div>
      </InlineStack>
    </div>
  );
}

function Photos({ loc, photos, onChanged, notify }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const current = (photos || []).find((p) => p.url === loc.image_url);
  const others = (photos || []).filter((p) => p.url !== loc.image_url);
  return (
    <div className="uh-sl-photo">
      <div className="uh-sl-photo-img">{loc.image_url ? <img src={loc.image_url} alt={`${loc.name}`} /> : <span className="uh-faint">No photo yet</span>}</div>
      <div className="uh-sl-photo-side">
        <div className="uh-ia-row">
          <button type="button" className="uh-btn slim" disabled={busy || !loc.id} onClick={() => input.current && input.current.click()}>{loc.image_url ? 'Replace photo' : 'Upload photo'}</button>
          {current && <button type="button" className="uh-btn slim" disabled={busy} onClick={() => act(() => slApi.removePhoto(loc.id, current.id))}>Remove</button>}
          {loc.image_url && <a className="uh-btn slim" href={loc.image_url} target="_blank" rel="noreferrer">Open full size</a>}
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) act(() => slApi.uploadPhoto(loc.id, f).then(() => notify('Photo uploaded'))); }} />
        </div>
        <div className="uh-ia-hint">{current ? `${current.file_name}${current.width ? ` · ${current.width} × ${current.height}` : ''}, uploaded ${new Date(current.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : (loc.image_url ? 'From the old app; upload a file to keep a copy in Shopify Files.' : 'JPG, PNG or WebP. Landscape prints best on the store page.')}{!loc.id && ' Save the location first to upload a photo.'}</div>
        {others.length > 0 && (
          <div className="uh-sl-thumbs">
            {others.map((p) => (
              <div key={p.id} className="uh-sl-thumb">
                <img src={p.url} alt={p.file_name} />
                <div className="uh-ia-row"><button type="button" className="uh-btn slim" disabled={busy} onClick={() => act(() => slApi.usePhoto(loc.id, p.id))}>Use</button><button type="button" className="uh-btn slim" disabled={busy} onClick={() => act(() => slApi.removePhoto(loc.id, p.id))}>Remove</button></div>
              </div>
            ))}
          </div>
        )}
        {err && <div className="uh-ia-bad">{err}</div>}
      </div>
    </div>
  );
}

export default function LocationPage({ id, ctx }) {
  const navigate = useNavigate();
  const isNew = id === null;
  const [loc, setLoc] = useState(null);
  const [others, setOthers] = useState([]);
  useEffect(() => { slApi.locations().then((r) => setOthers(r.locations)).catch(() => {}); }, []);
  const [form, setForm] = useState(blank());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const load = () => slApi.location(id).then((l) => { setLoc(l); setForm({ ...blank(), ...Object.fromEntries(Object.entries(l).filter(([k]) => k in blank())), latitude: l.latitude ?? '', longitude: l.longitude ?? '' }); }).catch((e) => setError(e.message));
  useEffect(() => { if (!isNew) load(); }, [id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const text = (k, label, props = {}) => <Field label={label} {...props}><input className="uh-ia-inp" value={form[k] || ''} onChange={(e) => setF(k, e.target.value)} placeholder={props.placeholder} /></Field>;
  const body = () => {
    const b = { ...form, priority: Number(form.priority) || 0 };
    if (b.latitude === '' || b.longitude === '') { delete b.latitude; delete b.longitude; } else { b.latitude = Number(b.latitude); b.longitude = Number(b.longitude); }
    if (!isNew && loc && Number(b.latitude) === Number(loc.latitude) && Number(b.longitude) === Number(loc.longitude)) { delete b.latitude; delete b.longitude; }   // untouched: let the address drive geocoding
    return b;
  };
  const save = async (thenPublish) => {
    setBusy(thenPublish ? 'publish' : 'save'); setError('');
    try {
      const saved = isNew ? await slApi.create(body()) : await slApi.update(id, body());
      if (saved.geocode_error) ctx.notify(`Saved, but not geocoded: ${saved.geocode_error}`);
      if (thenPublish) {
        const r = await slApi.publishOne(saved.id);
        const ok = r.stores.filter((s) => s.ok).length; const bad = r.stores.filter((s) => !s.ok);
        ctx.notify(bad.length ? `Published to ${ok}; failed on ${bad.map((s) => s.domain).join(', ')}` : `Saved and published to ${ok} store${ok === 1 ? '' : 's'}`);
      } else ctx.notify('Saved');
      ctx.reloadMeta();
      if (isNew) navigate(`/store-locator/locations/${saved.id}`, { replace: true }); else load();
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${form.name}? It leaves every store at the next publish.`)) return;
    setBusy('delete'); try { await slApi.remove(id); ctx.reloadMeta(); navigate('/store-locator/locations'); } catch (e) { setError(e.message); setBusy(''); }
  };
  const regeocode = async () => { setBusy('geo'); setError(''); try { const l = await slApi.geocode(id); setForm((f) => ({ ...f, latitude: l.latitude, longitude: l.longitude })); setLoc(l); ctx.notify('Coordinates refreshed'); } catch (e) { setError(e.message); } finally { setBusy(''); } };
  const allTags = (ctx.meta && ctx.meta.tags) || [];
  const stores = (ctx.meta && ctx.meta.stores) || [];
  const mapsUrl = form.latitude !== '' ? `https://www.google.com/maps?q=${form.latitude},${form.longitude}` : `https://www.google.com/maps?q=${encodeURIComponent([form.address_line_1, form.city, form.region, form.postal_code].filter(Boolean).join(', '))}`;
  return (
    <section className="uh-page uh-sl-page">
      <div className="uh-ph">
        <div>
          <div className="uh-crumb"><button type="button" className="uh-link" onClick={() => navigate('/store-locator/locations')}>Store Locator</button> › {isNew ? 'New location' : form.name || '…'}</div>
          <h1 className="uh-h1">{isNew ? 'Add a location' : form.name || 'Location'}</h1>
          {!isNew && loc && <div className="uh-sub">{loc.address_line_1}, {loc.city}, {loc.region} · <a className="uh-link" href={mapsUrl} target="_blank" rel="noreferrer">Open in Google Maps</a></div>}
        </div>
        <div className="uh-ia-row">
          <button type="button" className="uh-btn primary" disabled={!!busy} onClick={() => save(true)}>{busy === 'publish' ? 'Publishing…' : 'Save and publish'}</button>
          <button type="button" className="uh-btn" disabled={!!busy} onClick={() => save(false)}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      <Alert>{error}</Alert>
      <div className="uh-sl-cols">
        <div className="uh-sl-main">
          <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Store</h2></div><div className="uh-card-b uh-ia-frow">
            {text('name', 'Name')}
            {text('handle', 'Handle (URL)', { hint: 'Blank: made from the name. /pages/stores/<handle>' })}
            <Field label="Status"><select className="uh-select" value={form.status} onChange={(e) => setF('status', e.target.value)}><option value="published">Published</option><option value="draft">Draft (kept here, not on the sites)</option></select></Field>
            <Field label="Priority" hint="Higher sorts first in lists"><input className="uh-ia-inp" type="number" value={form.priority} onChange={(e) => setF('priority', e.target.value)} /></Field>
            <div className="uh-ia-fld uh-span2"><span className="lab">Tags</span><TagsField tags={form.tags} all={allTags.map((t) => t.title)} onChange={(v) => setF('tags', v)} /></div>
          </div></div>
          <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Address</h2></div><div className="uh-card-b">
            <div className="uh-ia-frow">
              {text('address_line_1', 'Address line 1', { span2: true })}
              {text('address_line_2', 'Address line 2')}
              {text('postal_code', 'ZIP')}
              {text('city', 'City')}
              {text('region', 'State', { hint: 'Spelled out: New Jersey' })}
              {text('country', 'Country')}
              {text('timezone', 'Time zone', { hint: 'Set from the state; change for the odd case' })}
              <Field label="Latitude"><input className="uh-ia-inp" value={form.latitude} onChange={(e) => setF('latitude', e.target.value)} /></Field>
              <Field label="Longitude"><input className="uh-ia-inp" value={form.longitude} onChange={(e) => setF('longitude', e.target.value)} /></Field>
            </div>
            <div className="uh-ia-row" style={{ marginTop: 10 }}>
              {loc && loc.geocoded_at ? <StatusPill tone="ful">Geocoded {new Date(loc.geocoded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} from the address</StatusPill> : <StatusPill tone="neu">{form.latitude !== '' ? 'Coordinates from the import' : 'Not geocoded yet'}</StatusPill>}
              {!isNew && <button type="button" className="uh-btn slim" disabled={!!busy} onClick={regeocode}>{busy === 'geo' ? 'Looking up…' : 'Re-geocode from the address'}</button>}
              <span className="uh-ia-hint">A changed address is geocoded on save unless you type coordinates.</span>
            </div>
          </div></div>
          <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Hours</h2></div><div className="uh-card-b">
            <HoursGrid hours={form.hours} onChange={(h) => setF('hours', h)} others={others} selfId={isNew ? null : id} />
            {loc && loc.special_hours && loc.special_hours.length > 0 && (
              <div className="uh-ia-hint" style={{ marginTop: 10 }}>Special hours that apply: {loc.special_hours.map((s) => `${span(s.date)} ${s.status === 'closed' ? 'closed' : `${clock(s.open)} – ${clock(s.close)}`}`).join(' · ')}</div>
            )}
          </div></div>
          <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Contact and page</h2></div><div className="uh-card-b uh-ia-frow">
            {text('phone', 'Phone')}{text('email', 'Email')}{text('website', 'Website')}
            {text('page_title', 'Page title', { span2: true })}
            <Field label="Page description" span2><textarea className="uh-ia-inp uh-sl-ta" rows={4} value={form.page_description} onChange={(e) => setF('page_description', e.target.value)} /></Field>
            {text('meta_title', 'Meta title', { placeholder: 'Defaults to the page title' })}
            {text('meta_description', 'Meta description', { placeholder: 'Defaults to the page description' })}
            {text('designation', 'Designation')}
            {text('announcements', 'Announcements', { hint: 'Always shown on the store page; dated messages go on the Notifications tab' })}
            {text('button_title_1', 'Button 1 title', { placeholder: 'e.g. Book a fitting' })}{text('button_url_1', 'Button 1 URL')}
            {text('button_title_2', 'Button 2 title')}{text('button_url_2', 'Button 2 URL')}
            {text('external_id', 'External ID')}
          </div></div>
          <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Photo</h2></div><div className="uh-card-b">
            <Photos loc={{ ...form, id: isNew ? null : id }} photos={loc && loc.photos} onChanged={load} notify={ctx.notify} />
          </div></div>
        </div>
        <div className="uh-sl-side">
          {!isNew && (
            <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Live pages</h2></div><div className="uh-card-b">
              {stores.map((s) => (
                <div key={s.domain} className="uh-sl-live">
                  <div><b>{s.public_host || s.domain.replace('.myshopify.com', '')}</b> {!s.enabled ? <StatusPill tone="neu">Off</StatusPill> : s.last_published_at ? <StatusPill tone={s.last_result && s.last_result.ok ? 'ful' : 'can'}>{new Date(s.last_published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</StatusPill> : <StatusPill tone="hold">Never published</StatusPill>}</div>
                  {s.enabled && s.public_host && form.status === 'published' && <a className="uh-link uh-ia-small" href={`https://${s.public_host}/pages/stores/${form.handle}`} target="_blank" rel="noreferrer">/pages/stores/{form.handle}</a>}
                </div>
              ))}
              {stores.length === 0 && <div className="uh-faint">No store set up yet (Stores & Publish tab).</div>}
            </div></div>
          )}
          {!isNew && loc && loc.notices && loc.notices.length > 0 && (
            <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2">Notifications</h2></div><div className="uh-card-b">
              {loc.notices.map((n) => <div key={n.id} className="uh-sl-live"><b>{n.title}</b><div className="uh-faint uh-ia-small">{span(n.shows_from, n.shows_until)} · {n.style}</div></div>)}
            </div></div>
          )}
          {!isNew && <div className="uh-card"><div className="uh-card-b"><button type="button" className="uh-btn slim danger" disabled={!!busy} onClick={remove}>Delete location</button></div></div>}
        </div>
      </div>
    </section>
  );
}
